import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDateTime, cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import {
    Bell,
    Gavel,
    MessageCircle,
    Package,
    Clock,
    AlertTriangle,
    Flag,
    CheckCircle,
    Trash2,
    CheckCheck,
    ShoppingBag,
    UserPlus,
    Star,
    CreditCard,
    Truck,
    TrendingUp,
    RefreshCw,
} from 'lucide-react';

const NotificationsPage = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { 
        unreadCount, 
        markAsRead, 
        markAllAsRead, 
        clearNotifications,
        removeNotification,
    } = useNotifications();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const PAGE_SIZE = 20;
    
    // Ref for infinite scroll detection
    const loadMoreRef = useRef(null);
    const containerRef = useRef(null);

    // Load notifications from API with pagination
    const loadNotifications = useCallback(async (skip = 0, append = false) => {
        if (!isAuthenticated) return;
        
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/notifications/my?skip=${skip}&limit=${PAGE_SIZE}`, 
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                const notificationsList = data.notifications || [];
                
                const formattedNotifications = notificationsList.map(n => ({
                    id: n.notification_id || n.id || Math.random().toString(),
                    notification_id: n.notification_id,
                    type: n.type,
                    title: n.title,
                    message: n.message,
                    link: n.link,
                    read: n.read || false,
                    timestamp: n.timestamp || n.created_at || new Date().toISOString()
                }));
                
                if (append) {
                    setNotifications(prev => [...prev, ...formattedNotifications]);
                } else {
                    setNotifications(formattedNotifications);
                }
                
                setTotal(data.total || 0);
                setHasMore(data.has_more || false);
            }
        } catch (error) {
            console.error('Error loading notifications:', error);
        }
    }, [isAuthenticated]);

    // Initial load on mount
    useEffect(() => {
        const loadData = async () => {
            if (isAuthenticated) {
                setLoading(true);
                await loadNotifications(0, false);
                setLoading(false);
            }
        };
        loadData();
    }, [isAuthenticated, loadNotifications]);

    // Infinite scroll observer
    useEffect(() => {
        if (!loadMoreRef.current || !hasMore) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    setLoadingMore(true);
                    loadNotifications(notifications.length, true).finally(() => {
                        setLoadingMore(false);
                    });
                }
            },
            { 
                threshold: 0.1,
                rootMargin: '100px'
            }
        );

        observer.observe(loadMoreRef.current);

        return () => observer.disconnect();
    }, [hasMore, loadingMore, loading, notifications.length, loadNotifications]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadNotifications(0, false);
        setRefreshing(false);
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.read) {
            await markAsRead(notification.id || notification.notification_id);
        }
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const handleDelete = async (e, notificationId) => {
        e.stopPropagation();
        await removeNotification(notificationId);
        // Also remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId && n.notification_id !== notificationId));
        setTotal(prev => Math.max(0, prev - 1));
    };

    const handleClearAll = async () => {
        await clearNotifications();
        setNotifications([]);
        setTotal(0);
        setHasMore(false);
    };

    const handleMarkAllAsRead = async () => {
        await markAllAsRead();
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'bid':
            case 'new_bid':
                return <Gavel className="w-5 h-5 text-primary" />;
            case 'outbid':
                return <TrendingUp className="w-5 h-5 text-orange-500" />;
            case 'message':
            case 'new_message':
                return <MessageCircle className="w-5 h-5 text-blue-500" />;
            case 'order':
            case 'order_update':
            case 'order_shipped':
                return <Package className="w-5 h-5 text-purple-500" />;
            case 'order_delivered':
            case 'order_completed':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'payment_confirmed':
            case 'sale_completed':
            case 'funds_released':
                return <Star className="w-5 h-5 text-green-500" />;
            case 'funds_returned':
            case 'refund':
                return <Star className="w-5 h-5 text-orange-500" />;
            case 'auction_ending':
            case 'time_extended':
                return <Clock className="w-5 h-5 text-yellow-500" />;
            case 'auction_won':
            case 'auction_end':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-orange-500" />;
            case 'ban':
            case 'suspended':
                return <AlertTriangle className="w-5 h-5 text-red-500" />;
            case 'report':
                return <Flag className="w-5 h-5 text-red-500" />;
            case 'review':
                return <Star className="w-5 h-5 text-yellow-500" />;
            case 'follow':
            case 'follower':
                return <UserPlus className="w-5 h-5 text-blue-500" />;
            default:
                return <Bell className="w-5 h-5 text-gray-500" />;
        }
    };

    // Sort notifications by timestamp (newest first) - already sorted from API
    const sortedNotifications = notifications;

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <p className="text-muted-foreground">Trebuie să fii autentificat pentru a vedea notificările.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pt-0 md:py-8" data-testid="notifications-page">
            <div className="container max-w-3xl mx-auto px-0 md:px-4">
                <Card className="rounded-2xl shadow-xs">
                    <CardHeader className="border-b">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CardTitle className="font-heading text-xl flex items-center gap-2">
                                    <Bell className="w-5 h-5" />
                                    Notificări
                                    {unreadCount > 0 && (
                                        <Badge variant="destructive" className="ml-2 rounded-full">
                                            {unreadCount}
                                        </Badge>
                                    )}
                                </CardTitle>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    className="rounded-full"
                                    data-testid="refresh-notifications"
                                >
                                    <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                                </Button>
                                {unreadCount > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleMarkAllAsRead}
                                        className="rounded-full text-xs"
                                        data-testid="mark-all-read"
                                    >
                                        <CheckCheck className="w-4 h-4 sm:mr-1" />
                                        <span className="hidden sm:inline">Marchează toate ca citite</span>
                                        <span className="sm:hidden">Citite</span>
                                    </Button>
                                )}
                                {notifications.length > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClearAll}
                                        className="rounded-full text-xs text-destructive hover:text-destructive"
                                        data-testid="clear-all"
                                    >
                                        <Trash2 className="w-4 h-4 sm:mr-1" />
                                        <span className="hidden sm:inline">Șterge toate</span>
                                        <span className="sm:hidden">Șterge</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <Bell className="w-12 h-12 text-muted-foreground/30 mb-4" />
                                <p className="text-muted-foreground">Nu ai notificări</p>
                                <p className="text-sm text-muted-foreground/70 mt-1">
                                    Notificările tale vor apărea aici
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {sortedNotifications.map((notification) => (
                                    <div
                                        key={notification.id || notification.notification_id}
                                        className={cn(
                                            "flex items-start gap-4 p-4 cursor-pointer transition-colors hover:bg-muted/50",
                                            !notification.read && "bg-gray-100"
                                        )}
                                        onClick={() => handleNotificationClick(notification)}
                                        data-testid={`notification-${notification.id || notification.notification_id}`}
                                    >
                                        <div className="shrink-0 mt-1">
                                            {getNotificationIcon(notification.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <p className={cn(
                                                        "text-sm",
                                                        !notification.read && "font-medium"
                                                    )}>
                                                        {notification.title}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground/70 mt-1">
                                                        {formatDateTime(notification.timestamp || notification.created_at)}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {!notification.read && (
                                                        <div className="w-2 h-2 rounded-full bg-primary" />
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                        onClick={(e) => handleDelete(e, notification.id || notification.notification_id)}
                                                        data-testid={`delete-notification-${notification.id || notification.notification_id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Load more trigger */}
                                {hasMore && (
                                    <div ref={loadMoreRef} className="flex flex-col items-center justify-center py-4 gap-2">
                                        {loadingMore ? (
                                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                        ) : (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setLoadingMore(true);
                                                    loadNotifications(notifications.length, true).finally(() => {
                                                        setLoadingMore(false);
                                                    });
                                                }}
                                                className="text-muted-foreground"
                                            >
                                                Încarcă mai multe
                                            </Button>
                                        )}
                                    </div>
                                )}
                                
                                {!hasMore && total > 20 && (
                                    <div className="text-center py-4 text-sm text-muted-foreground">
                                        Ai văzut toate cele {total} notificări
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default NotificationsPage;
